<template>
  <div class="wh-editor">
    <!-- Header -->
    <div class="hdr">
      <div class="hdr-id">
        <div class="name">{{ doctorName || "วันเวลาทำงาน" }}</div>
        <div class="sub">
          <span class="reg">ตารางประจำ</span>
          <span class="dotsep"></span>
          <span class="recsum" :title="recurringSummary">{{ recurringSummary || "—" }}</span>
        </div>
      </div>
      <div class="total">
        รวมเดือนนี้ <b class="tnum">{{ totalHoursValue }}</b> ชม.
      </div>
      <div class="hdr-right">
        <div class="monthnav">
          <button class="navbtn" title="เดือนก่อน" @click="prevMonth">
            <WhIcon name="chevL" size="17" />
          </button>
          <span class="mlabel">{{ monthLabel }}</span>
          <button class="navbtn" title="เดือนถัดไป" @click="nextMonth">
            <WhIcon name="chevR" size="17" />
          </button>
        </div>
        <button class="iconbtn" title="ปิด" @click="$emit('close')">
          <WhIcon name="x" size="18" />
        </button>
      </div>
    </div>

    <!-- Toolbar: legend + multi-select hint -->
    <div class="toolbar">
      <div class="legend">
        <span class="lg work"><i></i>ทำงาน</span>
        <span class="lg off"><i></i>วันหยุด</span>
        <span class="lg leave"><i></i>ลา</span>
        <span class="lg partial"><i></i>หยุดบางช่วง</span>
        <span class="lg extra"><i></i>เพิ่มกะ</span>
      </div>
      <div class="hint">
        <span class="kbd">⌘</span><span>คลิก เลือกหลายวัน</span>
        <span class="kbd">⇧</span><span>คลิก เลือกช่วง</span>
      </div>
    </div>

    <!-- Body -->
    <div class="body">
      <div class="cal-wrap">
        <CalendarGrid
          :year="year"
          :month="month"
          :days="resolvedDays"
          :selected-dates="selectedDates"
          @update:selectedDates="onSelectionChange"
        />
      </div>
      <div class="side">
        <SidePanel
          v-if="selectedDates.length"
          :dates="selectedDates"
          :day-data="dayData"
          :branch-info="branchInfo"
          :rooms="getRoomList"
          @create="onCreate"
          @edit-recurring="onEditRecurring"
          @delete-recurring="onDeleteRecurring"
          @edit-exception="onEditException"
          @delete-exception="onDeleteException"
          @leave="onLeave"
        />
        <div v-else class="empty">
          <div class="ill"><WhIcon name="calendar" size="42" /></div>
          <div>
            <h4>เลือกวันที่บนปฏิทิน</h4>
            <p>แตะวันที่เพื่อดูหรือแก้ไขเวลาทำงาน เพิ่มกะ หรือบันทึกวันลา</p>
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="isLoading"></Loading>
    <Dialog ref="Dialog"></Dialog>
    <ScopeDialog ref="ScopeDialog"></ScopeDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import CalendarGrid from "@/components/workingHours/CalendarGrid";
import SidePanel from "@/components/workingHours/SidePanel";
import ScopeDialog from "@/components/workingHours/ScopeDialog";
import WhIcon from "@/components/workingHours/WhIcon";
import Loading from "@/components/Loading";
import Dialog from "@/components/modal/Dialog";
import { buildRruleStr, parseRruleStr } from "@/utils/recurrence";

// A recurring rule reduced to the wire shape the bulk replace-all endpoint wants.
const ruleToWire = (r) => ({
  rruleStr: r.rruleStr,
  startTime: r.startTime ? r.startTime.slice(0, 5) : null,
  endTime: r.endTime ? r.endTime.slice(0, 5) : null,
  roomId: r.roomId != null ? r.roomId : null,
});

// Build a wire rule from a ShiftForm payload starting on dtstart.
const ruleFromForm = (form, dtstart) => ({
  rruleStr: buildRruleStr({
    weekdays: form.weekdays,
    setpos: form.setpos,
    dtstart,
    until: form.until,
  }),
  startTime: form.from,
  endTime: form.to,
  roomId: form.roomId != null ? form.roomId : null,
});

// Build a `leave` override payload from a LeaveForm confirm payload.
// scope 'whole' → null range (drops the whole day); 'partial' → a time-range
// hole. Tolerant of a missing form (legacy one-click leave) → whole-day.
const leaveOverrideFromForm = (form, overrideDate, id) => {
  const partial = !!form && form.scope === "partial";
  const payload = {
    overrideDate,
    type: "leave",
    startTime: partial ? form.from : null,
    endTime: partial ? form.to : null,
    note: (form && form.note) || null,
  };
  if (id != null) payload.id = id;
  return payload;
};

// The working-hours editor body. Identity comes from props (clinicUrl /
// branchUrl / branchMemberId) so it can be hosted either by the full-page
// WorkingHours view or by the WorkingHoursModal. All recurrence/override logic
// lives here; the hosts only supply context and page chrome.
export default {
  name: "WorkingHoursEditor",
  components: { CalendarGrid, SidePanel, ScopeDialog, WhIcon, Loading, Dialog },
  props: {
    clinicUrl: { type: String, required: true },
    branchUrl: { type: String, required: true },
    branchMemberId: { type: [Number, String], required: true },
  },
  data() {
    return {
      year: moment().year(),
      month: moment().month() + 1, // 1..12
      selectedDates: [],
      isLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      schedule: "moduleWorkingHours/schedule",
      doctorName: "moduleWorkingHours/doctorName",
      recurringSummary: "moduleWorkingHours/recurringSummary",
      rules: "moduleWorkingHours/rules",
      dayEntriesFor: "moduleWorkingHours/dayEntriesFor",
      branchInfo: "moduleUser/getBranchInfo",
      getRoomList: "moduleUser/getRoomList",
    }),
    monthLabel() {
      // Pin locale: a global moment.locale("th") set elsewhere would otherwise
      // render Buddhist-era years and flip the header non-deterministically.
      return moment(`${this.year}-${String(this.month).padStart(2, "0")}-01`)
        .locale("en")
        .format("MMMM YYYY");
    },
    resolvedDays() {
      return (this.schedule && this.schedule.days) || [];
    },
    totalHoursLabel() {
      const m = (this.schedule && this.schedule.totalMinutes) || 0;
      const h = Math.floor(m / 60);
      const min = m % 60;
      return min === 0 ? `${h} ชม.` : `${h} ชม. ${min} นาที`;
    },
    // Compact hours figure for the header badge ("219" or "219.5").
    totalHoursValue() {
      const m = (this.schedule && this.schedule.totalMinutes) || 0;
      const h = Math.floor(m / 60);
      const min = m % 60;
      return min === 0 ? `${h}` : `${h}.${Math.round((min / 60) * 10)}`;
    },
    dayData() {
      // Editable entries for the single selected date (null while multi-select).
      if (this.selectedDates.length !== 1) return null;
      return this.dayEntriesFor(this.selectedDates[0]);
    },
  },
  watch: {
    branchMemberId: {
      handler() {
        this.reloadAll();
      },
      immediate: true,
    },
    year() {
      this.reloadSchedule();
    },
    month() {
      this.reloadSchedule();
    },
  },
  methods: {
    ...mapActions({
      fetchSchedule: "moduleWorkingHours/fetchSchedule",
      saveRules: "moduleWorkingHours/saveRules",
      upsertOverride: "moduleWorkingHours/upsertOverride",
      deleteOverride: "moduleWorkingHours/deleteOverride",
    }),
    async reloadAll() {
      await this.reloadSchedule();
    },
    async reloadSchedule() {
      const { clinicUrl, branchUrl, branchMemberId } = this;
      const from = moment(
        `${this.year}-${String(this.month).padStart(2, "0")}-01`
      ).format("YYYY-MM-DD");
      const to = moment(from).endOf("month").format("YYYY-MM-DD");
      this.isLoading = true;
      try {
        await this.fetchSchedule({
          clinicUrl,
          branchUrl,
          branchMemberId: Number(branchMemberId),
          from,
          to,
        });
      } catch (err) {
        this.showError(err);
      } finally {
        this.isLoading = false;
      }
    },
    showError(err) {
      this.$refs.Dialog.showAlertInfo(
        "เกิดข้อผิดพลาด",
        err.response?.data?.message || err.message,
        "error"
      );
    },
    // Run a sequence of store mutations, then reload once. Keeps every handler
    // consistent (loading state, error surfacing, single reload).
    async runMutation(fn) {
      this.isLoading = true;
      try {
        await fn();
        await this.reloadSchedule();
      } catch (err) {
        this.showError(err);
      } finally {
        this.isLoading = false;
      }
    },
    prevMonth() {
      if (this.month === 1) {
        this.year -= 1;
        this.month = 12;
      } else {
        this.month -= 1;
      }
      this.selectedDates = [];
    },
    nextMonth() {
      if (this.month === 12) {
        this.year += 1;
        this.month = 1;
      } else {
        this.month += 1;
      }
      this.selectedDates = [];
    },
    onSelectionChange(dates) {
      this.selectedDates = dates;
    },

    dayBefore(date) {
      return moment(date).subtract(1, "day").format("YYYY-MM-DD");
    },

    // --- Create (single one-off entries, or a new repeating rule) ---------
    onCreate({ dates, form }) {
      this.runMutation(async () => {
        if (form.mode === "repeat") {
          // Recompute the full rule list and replace-all.
          const dtstart = [...dates].sort()[0];
          const next = [...this.rules.map(ruleToWire), ruleFromForm(form, dtstart)];
          await this.saveRules(next);
        } else {
          // One "shift" override per selected date.
          for (const date of dates) {
            await this.upsertOverride({
              overrideDate: date,
              type: "shift",
              startTime: form.from,
              endTime: form.to,
              roomId: form.roomId,
              note: form.note,
            });
          }
        }
      });
    },

    // --- Edit a recurring shift (scope: day / following / all) ------------
    async onEditRecurring({ entry, date, form }) {
      const scope = await this.$refs.ScopeDialog.show({ date });
      if (!scope) return;
      this.runMutation(async () => {
        if (scope === "day") {
          // Change just this date: clear the recurring slot, add the new hours.
          await this.upsertOverride({
            overrideDate: date,
            type: "leave",
            startTime: null,
            endTime: null,
          });
          await this.upsertOverride({
            overrideDate: date,
            type: "shift",
            startTime: form.from,
            endTime: form.to,
            roomId: form.roomId,
            note: form.note,
          });
          return;
        }
        if (scope === "all") {
          const orig = parseRruleStr(entry.rruleStr);
          const next = this.rules.map((r) =>
            r.id === entry.ruleId
              ? ruleFromForm(form, orig.dtstart || date)
              : ruleToWire(r)
          );
          await this.saveRules(next);
          return;
        }
        // following: truncate the original at date-1, add a new rule from date.
        const orig = parseRruleStr(entry.rruleStr);
        const truncated = {
          rruleStr: buildRruleStr({
            weekdays: orig.weekdays,
            setpos: orig.setpos,
            dtstart: orig.dtstart || date,
            until: this.dayBefore(date),
          }),
          startTime: entry.start.slice(0, 5),
          endTime: entry.end.slice(0, 5),
          roomId: entry.roomId,
        };
        const next = this.rules.flatMap((r) =>
          r.id === entry.ruleId
            ? [truncated, ruleFromForm(form, date)]
            : [ruleToWire(r)]
        );
        await this.saveRules(next);
      });
    },

    // --- Delete a recurring shift (scope: day / following / all) ----------
    async onDeleteRecurring({ entry, date }) {
      const scope = await this.$refs.ScopeDialog.show({
        date,
        title: "ลบตารางประจำนี้กับ?",
      });
      if (!scope) return;
      this.runMutation(async () => {
        if (scope === "day") {
          await this.upsertOverride({
            overrideDate: date,
            type: "leave",
            startTime: null,
            endTime: null,
          });
          return;
        }
        if (scope === "all") {
          const next = this.rules
            .filter((r) => r.id !== entry.ruleId)
            .map(ruleToWire);
          await this.saveRules(next);
          return;
        }
        // following: truncate the series at date-1 (no replacement rule).
        const orig = parseRruleStr(entry.rruleStr);
        const truncated = {
          rruleStr: buildRruleStr({
            weekdays: orig.weekdays,
            setpos: orig.setpos,
            dtstart: orig.dtstart || date,
            until: this.dayBefore(date),
          }),
          startTime: entry.start.slice(0, 5),
          endTime: entry.end.slice(0, 5),
          roomId: entry.roomId,
        };
        const next = this.rules.map((r) =>
          r.id === entry.ruleId ? truncated : ruleToWire(r)
        );
        await this.saveRules(next);
      });
    },

    // --- Overrides (single-day, no scope) ---------------------------------
    onEditException({ exception, form }) {
      this.runMutation(() => {
        if (exception.type === "leave") {
          return this.upsertOverride(
            leaveOverrideFromForm(form, exception.overrideDate, exception.id)
          );
        }
        return this.upsertOverride({
          id: exception.id,
          overrideDate: exception.overrideDate,
          type: "shift",
          startTime: form.from,
          endTime: form.to,
          roomId: form.roomId,
          note: form.note,
        });
      });
    },
    onDeleteException(overrideId) {
      this.runMutation(() => this.deleteOverride(overrideId));
    },

    // --- Leave (whole-day or partial) for every selected date -------------
    onLeave({ dates, form }) {
      this.runMutation(async () => {
        for (const date of dates) {
          await this.upsertOverride(leaveOverrideFromForm(form, date));
        }
      });
    },
  },
};
</script>

<style scoped>
/* ---- design tokens (cascade to all working-hours descendants) ----
   --brand / --brand-ink / --brand-tint / --brand-tint-2 come from the
   tenant token layer (src/theme/_brand-tokens.scss, :root). */
.wh-editor {
  --scrim: 222 30% 14%;
  --surface: #ffffff;
  --surface-2: #f7f9fc;
  --surface-3: #eef2f8;
  --line: #e9edf3;
  --line-strong: #dde3ec;
  --ink: #192231;
  --ink-2: #3f4a5a;
  --muted: #6c7888;
  --faint: #9aa6b6;

  --work: #2563eb;
  --work-t: #eef3ff;
  --work-b: #dbe6ff;
  --extra: #15a34a;
  --extra-t: #e9f7ee;
  --extra-b: #cdeed8;
  --leave: #e5484d;
  --leave-t: #fdecec;
  --leave-b: #f8d3d4;
  --partial: #cf8a06;
  --partial-t: #fdf3da;
  --partial-b: #f4e3b0;
  --off: #8a96a6;
  --off-t: #f4f6f9;
  --off-b: #e8edf4;

  --r-lg: 18px;
  --r: 13px;
  --r-sm: 10px;
  --r-pill: 999px;
  --shadow-card: 0 1px 2px hsl(220 20% 30% / 0.06),
    0 1px 3px hsl(220 20% 30% / 0.05);
  --shadow-pop: 0 12px 32px -8px hsl(var(--scrim) / 0.28);
  --wh-font: var(
    --ci-font-family,
    "IBM Plex Sans Thai", "Noto Sans Thai", system-ui, sans-serif
  );
  --wh-num: var(
    --ci-font-family,
    "IBM Plex Sans", "IBM Plex Sans Thai", sans-serif
  );

  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  background: var(--surface);
  color: var(--ink);
  font-family: var(--wh-font);
  line-height: 1.5;
}
.wh-editor .tnum {
  font-family: var(--wh-num);
  font-variant-numeric: tabular-nums;
}

/* ---- header ---- */
.hdr {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 20px;
  border-bottom: 1px solid var(--line);
}
.hdr-id {
  flex: 1 1 auto;
  min-width: 0;
}
.hdr-id .name {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.hdr-id .sub {
  font-size: 13px;
  color: var(--muted);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  overflow: hidden;
}
.hdr-id .sub .reg {
  color: var(--ink-2);
  font-weight: 500;
  flex: none;
}
.hdr-id .sub .recsum {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dotsep {
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: var(--faint);
  display: inline-block;
}
.total {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  padding: 8px 15px;
  border-radius: var(--r-pill);
  background: var(--brand-tint);
  color: var(--brand-ink);
  font-weight: 600;
  font-size: 13px;
  border: 1px solid var(--brand-tint-2);
  white-space: nowrap;
  flex-shrink: 0;
}
.total b {
  font-size: 16px;
}
.hdr-right {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}
.monthnav {
  display: flex;
  align-items: center;
  gap: 2px;
  background: var(--surface-2);
  border: 1px solid var(--line);
  border-radius: var(--r-pill);
  padding: 3px;
}
.monthnav .mlabel {
  min-width: 132px;
  text-align: center;
  font-weight: 600;
  font-size: 14.5px;
  padding: 0 6px;
}
.navbtn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: 0.15s;
}
.navbtn:hover {
  background: var(--surface-3);
  color: var(--ink);
}
.iconbtn {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 0;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: 0.15s;
}
.iconbtn:hover {
  background: var(--surface-3);
  color: var(--ink);
}

/* ---- toolbar / legend ---- */
.toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 20px;
  border-bottom: 1px solid var(--line);
  background: var(--surface-2);
  flex-wrap: wrap;
}
.legend {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.lg {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  color: var(--ink-2);
  padding: 4px 10px 4px 8px;
  border-radius: var(--r-pill);
  white-space: nowrap;
}
.lg i {
  width: 11px;
  height: 11px;
  border-radius: 4px;
  flex: none;
  box-shadow: inset 0 0 0 1px hsl(220 20% 40% / 0.12);
}
.lg.work i {
  background: var(--work-t);
  box-shadow: inset 0 0 0 1.5px var(--work);
}
.lg.off i {
  background: var(--off-t);
  box-shadow: inset 0 0 0 1px var(--off-b);
}
.lg.leave i {
  background: var(--leave);
}
.lg.partial i {
  background: var(--partial);
}
.lg.extra i {
  background: var(--extra);
}
.hint {
  margin-left: auto;
  font-size: 12px;
  color: var(--muted);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.kbd {
  font-family: var(--wh-num);
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 6px;
  background: var(--surface);
  border: 1px solid var(--line-strong);
  box-shadow: 0 1px 0 var(--line-strong);
  color: var(--ink-2);
}

/* ---- body ---- */
.body {
  flex: 1;
  min-height: 0;
  display: flex;
}
.cal-wrap {
  flex: 1;
  min-width: 0;
  padding: 10px 16px;
  overflow: auto;
  display: flex;
  flex-direction: column;
}
.side {
  width: 392px;
  min-width: 392px;
  border-left: 1px solid var(--line);
  background: var(--surface-2);
  overflow: auto;
}
@media (max-width: 900px) {
  .side {
    width: 340px;
    min-width: 340px;
  }
}

/* ---- empty side-panel state ---- */
.empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  text-align: center;
  padding: 40px 36px;
  color: var(--muted);
}
.empty .ill {
  width: 86px;
  height: 86px;
  border-radius: 24px;
  background: var(--surface-3);
  display: grid;
  place-items: center;
  color: var(--brand);
  box-shadow: inset 0 0 0 1px var(--line);
}
.empty h4 {
  margin: 0;
  font-size: 15.5px;
  color: var(--ink);
  font-weight: 600;
}
.empty p {
  margin: 0;
  font-size: 13px;
  max-width: 230px;
}
</style>
